/**
 * Feedback Command
 * Submit bug reports, feature requests, and suggestions
 * Supports both interactive and non-interactive modes
 */
import { Command } from 'commander';
import { select, input, confirm } from '@inquirer/prompts';
import { ConfigService } from '../services/config.service.js';
import { ValidationError } from '../errors/index.js';
import os from 'os';
import { promises as fs } from 'fs';
import { join } from 'path';
import { selectTheme } from '../utils/prompt-theme.js';

interface FeedbackOptions {
    type?: string;
    title?: string;
    message?: string;
    context?: string;
    noContext?: boolean;
    json?: boolean;
}

interface FeedbackContext {
    os?: string;
    cli_version?: string;
    project_name?: string;
    error_log?: string;
}

/**
 * Collect automatic context
 */
async function collectContext(): Promise<FeedbackContext> {
    const context: FeedbackContext = {
        os: `${os.platform()} ${os.release()}`,
    };

    // Get CLI version from package.json
    try {
        const packageJsonPath = new URL('../../package.json', import.meta.url);
        const packageJson = JSON.parse(await fs.readFile(packageJsonPath, 'utf-8'));
        context.cli_version = packageJson.version;
    } catch {
        // Ignore if can't read
    }

    // Get project name from package.json in current directory
    try {
        const projectPackageJson = await fs.readFile(join(process.cwd(), 'package.json'), 'utf-8');
        const projectData = JSON.parse(projectPackageJson);
        context.project_name = projectData.name;
    } catch {
        // Ignore if not in a project directory
    }

    return context;
}

/**
 * Submit feedback to API
 */
async function submitFeedback(
    apiUrl: string,
    accessKey: string,
    data: {
        type: string;
        title: string;
        message: string;
        context?: FeedbackContext;
    }
): Promise<{ success: boolean; feedback_id?: string; error?: string }> {
    const response = await fetch(`${apiUrl}/api/feedback`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'JAI1-Access-Key': accessKey,
        },
        body: JSON.stringify({
            type: data.type,
            title: data.title,
            message: data.message,
            context: data.context,
            source: 'cli',
        }),
    });

    const result = await response.json();

    if (!response.ok) {
        return { success: false, error: result.error || 'Failed to submit feedback' };
    }

    return { success: true, feedback_id: result.data.feedback_id };
}

/**
 * Handle interactive feedback submission
 */
async function handleInteractiveFeedback(config: any): Promise<void> {
    console.log('📝 Submit Feedback to Jai1\n');

    // Select type
    const type = await select({
        message: 'What type of feedback?',
        choices: [
            {
                name: '🐛 Bug Report - Report an error or issue',
                value: 'bug',
            },
            {
                name: '💡 Feature Request - Suggest a new feature',
                value: 'feature',
            },
            {
                name: '📝 Suggestion - Share ideas or improvements',
                value: 'suggestion',
            },
        ],
        theme: selectTheme,
    });

    // Get title
    const title = await input({
        message: 'Title (max 200 characters):',
        validate: (value) => {
            if (!value || value.trim().length === 0) {
                return 'Title is required';
            }
            if (value.length > 200) {
                return 'Title must be 200 characters or less';
            }
            return true;
        },
    });

    // Get message
    const message = await input({
        message: 'Description (max 4000 characters):',
        validate: (value) => {
            if (!value || value.trim().length === 0) {
                return 'Description is required';
            }
            if (value.length > 4000) {
                return 'Description must be 4000 characters or less';
            }
            return true;
        },
    });

    // Collect context (always included by default)
    const context = await collectContext();

    // Show summary
    console.log('\n📋 Summary:\n');
    console.log(`Type:        ${type}`);
    console.log(`Title:       ${title}`);
    console.log(`Description: ${message.substring(0, 100)}${message.length > 100 ? '...' : ''}`);
    if (context) {
        console.log(`Context:     OS: ${context.os}, CLI: ${context.cli_version || 'unknown'}`);
    }

    const confirmed = await confirm({
        message: '\nSubmit feedback?',
        default: true,
    });

    if (!confirmed) {
        console.log('\n❌ Cancelled.');
        return;
    }

    // Submit
    console.log('\n⏳ Submitting feedback...');

    try {
        const result = await submitFeedback(config.apiUrl, config.accessKey, {
            type,
            title: title.trim(),
            message: message.trim(),
            context,
        });

        if (result.success) {
            console.log(`\n✅ Feedback submitted successfully!`);
            console.log(`   Feedback ID: ${result.feedback_id}\n`);
            console.log('   Thank you for your feedback! We will review it soon.');
        } else {
            console.error(`\n❌ Failed to submit feedback: ${result.error}`);
            process.exit(1);
        }
    } catch (error) {
        console.error('\n❌ Network error:', error instanceof Error ? error.message : String(error));
        process.exit(1);
    }
}

/**
 * Handle non-interactive feedback submission
 */
async function handleNonInteractiveFeedback(config: any, options: FeedbackOptions): Promise<void> {
    // Validate required fields
    if (!options.type || !options.title || !options.message) {
        throw new ValidationError('Missing required fields: --type, --title, --message');
    }

    // Validate type
    const validTypes = ['bug', 'feature', 'suggestion'];
    if (!validTypes.includes(options.type)) {
        throw new ValidationError(`Invalid type. Must be one of: ${validTypes.join(', ')}`);
    }

    // Validate lengths
    if (options.title.length > 200) {
        throw new ValidationError('Title must be 200 characters or less');
    }

    if (options.message.length > 4000) {
        throw new ValidationError('Message must be 4000 characters or less');
    }

    // Collect context
    let context: FeedbackContext | undefined;
    if (!options.noContext) {
        context = await collectContext();

        // Merge custom context if provided
        if (options.context) {
            try {
                const customContext = JSON.parse(options.context);
                context = { ...context, ...customContext };
            } catch {
                throw new ValidationError('Invalid JSON in --context');
            }
        }
    }

    // Submit
    try {
        const result = await submitFeedback(config.apiUrl, config.accessKey, {
            type: options.type,
            title: options.title.trim(),
            message: options.message.trim(),
            context,
        });

        if (result.success) {
            if (options.json) {
                console.log(JSON.stringify({ success: true, feedback_id: result.feedback_id }));
            } else {
                console.log(`✅ Feedback submitted: ${result.feedback_id}`);
            }
        } else {
            if (options.json) {
                console.log(JSON.stringify({ success: false, error: result.error }));
            } else {
                console.error(`❌ Error: ${result.error}`);
            }
            process.exit(1);
        }
    } catch (error) {
        if (options.json) {
            console.log(JSON.stringify({ success: false, error: error instanceof Error ? error.message : 'Unknown error' }));
        } else {
            console.error('❌ Error:', error instanceof Error ? error.message : String(error));
        }
        process.exit(1);
    }
}

/**
 * Handle feedback command
 */
async function handleFeedbackCommand(options: FeedbackOptions): Promise<void> {
    const configService = new ConfigService();
    const config = await configService.load();

    if (!config) {
        throw new ValidationError('Not initialized. Run "jai1 auth" first.');
    }

    // Check if stdin has data (for piping)
    let stdinMessage = '';
    if (!process.stdin.isTTY && !options.message) {
        // Read from stdin
        const chunks: Buffer[] = [];
        for await (const chunk of process.stdin) {
            chunks.push(chunk);
        }
        stdinMessage = Buffer.concat(chunks).toString('utf-8').trim();
    }

    // If stdin provided, use non-interactive mode
    if (stdinMessage) {
        options.message = stdinMessage;
    }

    // Determine mode
    const isInteractive = !options.type && !options.title && !options.message;

    if (isInteractive) {
        await handleInteractiveFeedback(config);
    } else {
        await handleNonInteractiveFeedback(config, options);
    }
}

/**
 * Create feedback command
 */
export function createFeedbackCommand(): Command {
    const cmd = new Command('feedback')
        .alias('report')
        .description('Submit bug reports, feature requests, or suggestions')
        .option('--type <type>', 'Feedback type: bug, feature, suggestion')
        .option('--title <title>', 'Feedback title (max 200 chars)')
        .option('--message <message>', 'Feedback message (max 4000 chars)')
        .option('--context <json>', 'Additional context as JSON')
        .option('--no-context', 'Do not include automatic context')
        .option('--json', 'Output JSON format')
        .action(async (options: FeedbackOptions) => {
            await handleFeedbackCommand(options);
        });

    return cmd;
}
